import * as React from 'react';
import cn from 'classnames';
import { FC } from 'react';
import { useMediaQuery } from 'react-responsive';
import { ClassNameMap, DialogClassKey, Dialog, DialogProps, PaperProps } from '@mui/material';
import { useTheme } from '../../../theme';
import { MOBILE_MIN_WIDTH } from '../../../utils';
import { GrowTransition, SlideUpTransition } from '../Transitions';
import { useSwipeDown } from '../hooks';
import CloseButton from '../../../BaseComponents/Button/components/CloseButton/CloseButton';
import { OwnStyles } from '../types';
import { useBlurActiveElement } from '../../../hooks';

export type ModalXProps = {
	classes?: Partial<ClassNameMap<DialogClassKey>> & OwnStyles;
	scroll?: DialogProps['scroll'];
	isOpen?: boolean;
	onClose?: () => void;
	children?: React.ReactNode;
	title?: React.ReactElement | string;
	footer?: React.ReactElement;
	/** Скрывает кнопку и вызов onClose при закрытии Dialog */
	ignoreClose?: boolean;
	size?: 'small' | 'medium' | 'large' | 'xlarge' | 'fullscreen';
	noPadding?: boolean;
	keepMounted?: DialogProps['keepMounted'];
	/** Нужно ли закрывать модалку свайпом */
	enableSwipeToClose?: boolean;
	paperProps?: PaperProps;
};

const ModalX: FC<ModalXProps> = ({
	size = 'small',
	classes,
	children,
	isOpen = false,
	onClose,
	scroll = 'paper',
	title,
	footer,
	ignoreClose,
	noPadding,
	keepMounted = true,
	enableSwipeToClose = true,
	paperProps
}) => {
	const theme = useTheme('BaseComponents').ModalX;
	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	/**
	 * WEBSKY-5566:
	 * Перед закрытием модалки/диалогового окна, снимаем фокус с активного элемента (если есть),
	 * чтобы избежать ошибки "blocked aria-hidden".
	 */
	useBlurActiveElement(isOpen);

	const { handlers, contentRef, swipeBlockRef, swipeFxRef, paperRef } = useSwipeDown({
		scroll,
		onClose,
		isMobile,
		isOpen,
		enableSwipeToClose
	});

	const isFullscreen = size === 'fullscreen';
	const showCloseButton = Boolean(onClose && !ignoreClose);
	const isSwipeEnabled = Boolean(enableSwipeToClose && isMobile && onClose);
	const showSwipeBlock = isSwipeEnabled && !isFullscreen;

	/* Закрытие по свайпу добавляем на все диалоговое окно */
	const dialogHandlers = scroll === 'paper' && isSwipeEnabled ? { ...handlers } : null;
	/* Закрытие по свайпу добавляем только на ручку-свайп */
	const swipeBlockHandlers = scroll === 'body' && isSwipeEnabled ? { ...handlers } : null;

	return (
		<Dialog
			{...dialogHandlers}
			open={isOpen}
			onClose={showCloseButton ? onClose : undefined}
			keepMounted={keepMounted}
			closeAfterTransition={false}
			disableEnforceFocus={isFullscreen}
			fullScreen={isFullscreen}
			scroll={scroll}
			fullWidth
			classes={{
				root: cn(theme.root, classes?.root),
				container: cn(theme.dialog_container, classes?.dialog_container),
				paper: cn(theme.paper, theme[size], classes?.paper, {
					[theme.paper__fullScreen]: isFullscreen,
					[theme.paper__noPadding]: noPadding
				}),
				paperScrollBody: theme.paper_paperScrollBody
			}}
			TransitionComponent={isMobile || isFullscreen ? SlideUpTransition : GrowTransition}
			PaperProps={{
				ref: paperRef,
				...paperProps
			}}
		>
			{showSwipeBlock && (
				<div ref={swipeBlockRef} className={cn(theme.swipeBlock, classes?.swipeBlock)} {...swipeBlockHandlers}>
					<div className={theme.swipeBlock_line} ref={swipeFxRef}></div>
				</div>
			)}

			{showCloseButton && (
				<CloseButton
					onClick={onClose}
					variant="closePopup"
					classes={{ root: cn(theme.closeButton, classes?.closeButton) }}
				/>
			)}

			<div className={cn(theme.top_wrapper, classes?.top_wrapper)} onMouseDown={e => e.stopPropagation()}>
				{title}
				<div className={cn(theme.content, classes?.content)} ref={contentRef}>
					{children}
				</div>
			</div>

			{footer}
		</Dialog>
	);
};

export default ModalX;
